import { useEffect, useRef, useState } from 'react'
import { Download, ImageOff, RotateCw, Scissors, Upload, X } from 'lucide-react'
import { useRemoveBackground } from '@/api/endpoints/backgroundRemoval'
import { Button } from '@/components/ui/Button'
import { PageHeader } from '@/components/ds/PageHeader'
import { Section } from '@/components/ds/Section'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/errors'
import styles from './BackgroundRemovalPage.module.scss'

// Mirror the backend allowlist + 15MB cap (config/settings.py). Validating
// client-side gives instant feedback; the server still enforces both.
const ACCEPTED_MIME = ['image/png', 'image/jpeg', 'image/webp']
const MAX_BYTES = 15 * 1024 * 1024

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function BackgroundRemovalPage() {
  const [file, setFile] = useState<File | null>(null)
  const [sourceUrl, setSourceUrl] = useState<string | null>(null)
  const [resultUrl, setResultUrl] = useState<string | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const remove = useRemoveBackground()

  // Revoke object URLs on replacement/unmount so blob URLs don't leak.
  useEffect(() => {
    return () => {
      if (sourceUrl) URL.revokeObjectURL(sourceUrl)
    }
  }, [sourceUrl])
  useEffect(() => {
    return () => {
      if (resultUrl) URL.revokeObjectURL(resultUrl)
    }
  }, [resultUrl])

  const acceptFile = (f: File) => {
    setLocalError(null)
    remove.reset()
    if (!ACCEPTED_MIME.includes(f.type)) {
      setLocalError('Formato não suportado. Use PNG, JPEG ou WebP.')
      return
    }
    if (f.size > MAX_BYTES) {
      setLocalError('Arquivo muito grande (máx 15MB).')
      return
    }
    setResultUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
    setSourceUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return URL.createObjectURL(f)
    })
    setFile(f)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) acceptFile(f)
    // Reset so picking the same file again re-fires onChange.
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const f = e.dataTransfer.files[0]
    if (f) acceptFile(f)
  }

  const handleSubmit = () => {
    if (!file || remove.isPending) return
    remove.mutate(file, {
      onSuccess: ({ url }) => {
        setResultUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev)
          return url
        })
      },
    })
  }

  const handleReset = () => {
    remove.reset()
    setLocalError(null)
    setFile(null)
    setSourceUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
    setResultUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
  }

  const apiError = remove.isError ? errorMessage(remove.error, 'Falha ao remover fundo.') : null

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Tools"
        title="Remoção de fundo"
        description="Envie uma imagem — o sistema detecta o objeto principal, remove o resto e devolve um PNG com fundo transparente."
      />

      {/* INPUT */}
      <Section title="Imagem" description="PNG, JPEG ou WebP, até 15MB.">
        <div
          className={cn(styles.dropzone, dragging && styles.dropzoneActive, sourceUrl && styles.dropzoneCompact)}
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => {
            setDragging(false)
          }}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              inputRef.current?.click()
            }
          }}
          aria-label="Selecionar imagem"
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_MIME.join(',')}
            className={styles.hiddenInput}
            onChange={handleInputChange}
          />
          <Upload className={styles.dropIcon} aria-hidden />
          {file ? (
            <p className={styles.dropText}>
              <strong>{file.name}</strong> · {formatBytes(file.size)} — clique ou arraste para trocar
            </p>
          ) : (
            <p className={styles.dropText}>Clique para selecionar ou arraste uma imagem aqui</p>
          )}
        </div>

        {localError ? <p className={styles.error}>{localError}</p> : null}

        <div className={styles.submitRow}>
          <Button
            type="button"
            variant="primary"
            leadingIcon={<Scissors />}
            onClick={handleSubmit}
            disabled={!file || remove.isPending}
          >
            {remove.isPending ? 'Removendo fundo…' : 'Remover fundo'}
          </Button>
          {file || resultUrl ? (
            <Button type="button" variant="ghost" leadingIcon={<X />} onClick={handleReset}>
              Limpar
            </Button>
          ) : null}
        </div>
      </Section>

      {/* RESULT */}
      {sourceUrl ? (
        <Section title="Resultado">
          <div role="status" aria-live="polite" className={styles.srOnly}>
            {remove.isPending ? 'Processando…' : apiError ? `Erro: ${apiError}` : resultUrl ? 'Fundo removido.' : ''}
          </div>

          {apiError && !remove.isPending ? (
            <div className={styles.errorCard}>
              <X className={styles.errorIcon} aria-hidden />
              <div className={styles.errorBody}>
                <strong>Falha ao processar</strong>
                <p>{apiError}</p>
              </div>
              <Button variant="secondary" size="sm" leadingIcon={<RotateCw />} onClick={handleSubmit}>
                Tentar de novo
              </Button>
            </div>
          ) : null}

          <div className={styles.compareGrid}>
            <figure className={styles.compareCol}>
              <figcaption className={styles.compareLabel}>Original</figcaption>
              <div className={styles.imageFrame}>
                <img src={sourceUrl} alt="Imagem original" className={styles.image} />
              </div>
            </figure>

            <figure className={styles.compareCol}>
              <figcaption className={styles.compareLabel}>Sem fundo</figcaption>
              <div className={cn(styles.imageFrame, styles.checkerboard)}>
                {remove.isPending ? (
                  <div className={styles.skeleton}>
                    <span className={styles.skeletonShimmer} />
                    <span className={styles.skeletonMessage}>Removendo fundo…</span>
                  </div>
                ) : resultUrl ? (
                  <img src={resultUrl} alt="Imagem sem fundo" className={styles.image} />
                ) : (
                  <div className={styles.placeholder}>
                    <ImageOff aria-hidden />
                    <span>Clique em “Remover fundo”.</span>
                  </div>
                )}
              </div>
            </figure>
          </div>

          {resultUrl ? (
            <div className={styles.resultActions}>
              <a href={resultUrl} download="sem-fundo.png" className={styles.downloadBtn} aria-label="Baixar PNG">
                <Download aria-hidden />
                <span>Baixar PNG</span>
              </a>
            </div>
          ) : null}
        </Section>
      ) : null}
    </div>
  )
}
